import React, { Component } from 'react';
// Composants
import { Button, Confirm, Label, Modal } from 'semantic-ui-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Librairies
import i18n from '../../locales/i18n';
import { faArrowsRotate } from '@fortawesome/pro-solid-svg-icons';
// Utils
import { getOfflineServices } from '../../utils/offline/OfflineServices';
import { getHandlers } from '../../utils/offline/handlers';
import Connectivity from '../../utils/offline/Connectivity';
import DatesUtil from '../../utils/DatesUtil';
import FormattersUtil from '../../utils/FormattersUtil';
import { getLidarCacheBytes, clearLidarCache } from '../../utils/lidar/LidarCache';
import { OFFLINE_TILES_CACHE } from '../../utils/offline/OfflineBaseLayers';

const STATUS_LABELS = () => ({
    pending: i18n.t("En attente"),
    sending: i18n.t("En cours d'envoi"),
    failed: i18n.t("En échec"),
    blocked: i18n.t("Bloquée")
});

// Écran « Synchronisation » : saisies en attente par projet, échecs (relance, abandon), journal, espace utilisé
export default class SyncModal extends Component {
    state = {
        ops: [],
        log: [],
        projectLabels: {},
        storage: null,
        lidarBytes: 0,
        tilesCount: 0,
        isPersisted: null,
        opToAbandon: null
    };

    render() {
        const { ops, log, projectLabels, storage, lidarBytes, tilesCount, isPersisted, opToAbandon } = this.state;
        const handlers = getHandlers();
        const statusLabels = STATUS_LABELS();
        const opsByProject = ops.reduce((groups, op) => ({ ...groups, [op.projectId]: [...(groups[op.projectId] || []), op] }), {});

        return (
            <Modal className='gr-modal' size='small' open onClose={this.props.onClose}>
                <Modal.Header>{i18n.t("Synchronisation")}</Modal.Header>
                <Modal.Content scrolling>
                    {!ops.length && <p>{i18n.t("Aucune saisie en attente")}</p>}
                    {Object.entries(opsByProject).map(([projectId, projectOps]) => {
                        // Attente résumée par type (des centaines de lignes identiques après une journée), seuls les échecs sont détaillés
                        const waitingOps = projectOps.filter(op => !['failed', 'blocked'].includes(op.status));
                        const failedOps = projectOps.filter(op => ['failed', 'blocked'].includes(op.status));
                        const summary = waitingOps.reduce((counts, op) => {
                            const label = this.describeOp(op, handlers);
                            counts[label] = (counts[label] || 0) + 1;
                            return counts;
                        }, {});
                        // Échec réseau : une ligne neutre, jamais le message d'Axios
                        const hasNetworkRetry = waitingOps.some(op => ['network', 'timeout'].includes(op.lastError?.reason));
                        return (
                            <div key={projectId} style={{ marginBottom: '15px' }}>
                                <h4>{projectLabels[projectId] || i18n.t("Projet")}</h4>
                                {Object.entries(summary).map(([label, count]) => (
                                    <div key={label}><small>{count} × {label}</small></div>
                                ))}
                                {hasNetworkRetry && <div><small style={{ color: 'var(--grey-100)' }}>{i18n.t("Connexion trop lente ou absente : nouvel essai automatique")}</small></div>}
                                {failedOps.map(op => (
                                    // flexWrap : sur téléphone, les boutons passent sous le texte
                                    <div key={op.id} className='gr-list-item gr-list-item--static' style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', padding: '6px 10px', marginBottom: '5px' }}>
                                        <div style={{ flex: '1 1 180px' }}>
                                            <div>{this.describeOp(op, handlers)}</div>
                                            <small style={{ color: 'var(--grey-100)' }}>{DatesUtil.getFormattedLocaleDateString(op.capturedAt, true)}</small>
                                            <div><small style={{ color: 'var(--red-100)' }} title={op.lastError?.message || undefined}>{this.describeError(op)}</small></div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Label size='small' color='red'>{statusLabels[op.status]}</Label>
                                            {/* Rouge plein : le rouge « basic » est illisible en thème sombre */}
                                            <Button size='mini' onClick={() => this.retry(op)}>{i18n.t("Relancer")}</Button>
                                            <Button size='mini' color='red' onClick={() => this.setState({ opToAbandon: op })}>{i18n.t("Abandonner")}</Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                    {log.length > 0 &&
                        <>
                            <h4>{i18n.t("Journal")}</h4>
                            {log.slice(0, 30).map(entry => (
                                <div key={entry.id} style={{ marginBottom: '6px' }}>
                                    <small style={{ color: 'var(--grey-100)' }}>{DatesUtil.getFormattedLocaleDateString(entry.at, true)} · </small>
                                    <small>{this.describeLogEntry(entry)}</small>
                                </div>
                            ))}
                        </>}
                    {storage &&
                        <p style={{ marginTop: '10px' }}>
                            <small>{i18n.t("Espace utilisé sur l'appareil")} : {FormattersUtil.numberPrettyBytesSI(storage.usage)} / {FormattersUtil.numberPrettyBytesSI(storage.quota)}</small>
                            {isPersisted !== null && <>
                                <br /><small>{i18n.t("Données protégées contre l'effacement automatique")} : {isPersisted ? i18n.t("oui") : i18n.t("non")}</small>
                            </>}
                            {lidarBytes > 0 && <>
                                <br /><small>{i18n.t("Dont données LiDAR gardées pour la visualiseuse")} : {FormattersUtil.numberPrettyBytesSI(lidarBytes)}</small>
                                <Button size='mini' basic style={{ marginLeft: '8px' }} onClick={this.clearLidar}>{i18n.t("Vider")}</Button>
                            </>}
                            {tilesCount > 0 && <>
                                <br /><small>{i18n.t("Dont tuiles du fond de carte hors-ligne")} : {tilesCount}</small>
                                <Button size='mini' basic style={{ marginLeft: '8px' }} onClick={this.clearTiles}>{i18n.t("Vider")}</Button>
                            </>}
                        </p>}
                </Modal.Content>
                <Modal.Actions>
                    {log.length > 0 && <Button basic onClick={this.clearLog}>{i18n.t("Vider le journal")}</Button>}
                    <Button color='blue' onClick={this.syncNow}>
                        <FontAwesomeIcon icon={faArrowsRotate} style={{ marginRight: '8px' }} />{i18n.t("Synchroniser maintenant")}
                    </Button>
                    <Button onClick={this.props.onClose}>{i18n.t("Fermer")}</Button>
                </Modal.Actions>
                <Confirm
                    open={!!opToAbandon}
                    header={i18n.t("Abandonner cette saisie ?")}
                    content={i18n.t("Elle ne sera jamais envoyée et sera définitivement perdue, ainsi que les saisies qui en dépendent.")}
                    cancelButton={i18n.t("Annuler")} confirmButton={i18n.t("Abandonner")}
                    onCancel={() => this.setState({ opToAbandon: null })}
                    onConfirm={this.abandon}
                />
            </Modal>
        );
    }

    componentDidMount = () => {
        // Rechargé à chaque changement de la file
        this.unsubscribe = getOfflineServices()?.outbox.subscribe(this.load);
        this.load();
        this.loadStorage();
    }

    componentWillUnmount = () => this.unsubscribe?.();

    // Le cache LiDAR (jusqu'à 300 Mo) partage le quota de la base locale
    loadStorage = () => {
        navigator.storage?.estimate?.().then(storage => this.setState({ storage })).catch(() => { });
        getLidarCacheBytes().then(lidarBytes => this.setState({ lidarBytes })).catch(() => { });
        if (typeof caches !== 'undefined') caches.open(OFFLINE_TILES_CACHE).then(cache => cache.keys()).then(keys => this.setState({ tilesCount: keys.length })).catch(() => { });
        navigator.storage?.persisted?.().then(isPersisted => this.setState({ isPersisted })).catch(() => { });
    }

    // Tuiles retéléchargées au prochain passage en ligne
    clearTiles = async () => {
        if (typeof caches !== 'undefined') await caches.delete(OFFLINE_TILES_CACHE).catch(() => { });
        this.setState({ tilesCount: 0 });
        this.loadStorage();
    }

    clearLidar = async () => {
        await clearLidarCache().catch(() => { });
        this.loadStorage();
    }

    // File, journal et noms des projets copiés
    load = async () => {
        const services = getOfflineServices();
        if (!services) return;
        const [ops, log, projects] = await Promise.all([services.outbox.list(), services.syncLog.list(), services.snapshot.listProjects()]);
        const projectLabels = Object.fromEntries(projects.map(record => [record.id, record.project?.label]));
        this.setState({ ops, log, projectLabels });
    }

    // Libellé du gestionnaire, type brut en dernier recours
    describeOp = (op, handlers) => {
        try { return handlers[op.type]?.describe?.(op) || op.type; }
        catch { return op.type; }
    }

    // Jamais le texte technique d'Axios ni celui de l'API (en anglais), gardé en infobulle pour le support
    describeError = (op) => {
        const error = op.lastError || {};
        if (op.status === 'blocked') return i18n.t("En attente d'une saisie dont elle dépend, qui a échoué");
        if (error.reason === 'handler_error') return i18n.t("Erreur de l'application : relancez ou contactez le support");
        if (error.message === 'Photo limit reached') return i18n.t("Nombre maximal de photos atteint pour cet élément : supprimez-en une en ligne, puis relancez");
        if (error.reason === 'no_response') return i18n.t("Le serveur ne répond pas à cette saisie : relancez ou contactez le support");
        if (error.status === 400) return i18n.t("Refus du serveur (donnée non valide) : abandonnez cette saisie ou contactez le support");
        if ([401, 403].includes(error.status)) return i18n.t("Refus du serveur : droits insuffisants sur ce projet");
        if (error.status === 404) return i18n.t("Refus du serveur : élément introuvable, sans doute supprimé");
        if (error.status) return i18n.t("Refus du serveur (erreur {{nb}})", { nb: error.status });
        return i18n.t("Envoi impossible");
    }

    // Libellé enregistré avec l'entrée : le journal ne garde pas la charge
    describeLogEntry = (entry) => {
        const operation = entry.label || entry.opType;
        if (entry.kind === 'discarded' && entry.reason === 'already_exists') return i18n.t("{{operation}} : déjà présente sur le serveur, rien n'a été envoyé", { operation });
        if (entry.kind === 'discarded' && ['already_done', 'already_deleted'].includes(entry.reason)) return i18n.t("{{operation}} : déjà faite sur le serveur", { operation });
        if (entry.kind === 'discarded') return i18n.t("{{operation}} : élément supprimé entre-temps, saisie écartée", { operation });
        return i18n.t("{{operation}} : saisie abandonnée", { operation });
    }

    // Sonde le réseau et envoie tout de suite, reprises comprises
    syncNow = () => {
        Connectivity.probe();
        getOfflineServices()?.engine?.trigger({ force: true });
    }

    retry = (op) => getOfflineServices()?.engine?.retry(op.id);

    abandon = async () => {
        const op = this.state.opToAbandon;
        this.setState({ opToAbandon: null });
        await getOfflineServices()?.engine?.abandon(op.id);
        this.load();
    }

    clearLog = async () => {
        await getOfflineServices()?.syncLog.clear();
        this.load();
    }
}
